import { EDITABLE_LINE_STATUSES, HIDDEN_CHARGE_TYPES } from './AdditionalChargeGrids.constants';
import {
  AcQuantityDimResult,
  AdditionalChargeColumn,
  AdditionalChargeColumnShape,
  AdditionalChargeDraftAttachments,
  AdditionalChargeEditableFieldKey,
  AdditionalChargeGroup,
  AdditionalChargeLineDraftEdits,
  AdditionalChargeLineDto,
  AdditionalChargeLinePatch,
  AdditionalChargeLinesPayload,
  AdditionalChargeQuantityDraftInputs,
} from './AdditionalChargeGrids.types';

/**
 * Typed-field keys `PATCH /additional-charges/lines/bulk` accepts (contract §3.3, extended by
 * ISS-291743). `attachment` rides separately. As of ISS-292248, `serviceStartDate` (PCD) and
 * `thirdPartyVendorName` are no longer here — PCD is server-derived and frozen at submit, and the
 * vendor name is now stamped server-side from the vendor-code master, so the server strips/rejects
 * both if sent. Every remaining editable field key is still wire-eligible.
 */
const BULK_PATCH_FIELD_KEYS: ReadonlyArray<AdditionalChargeEditableFieldKey> = [
  'rate',
  'currency',
  'thirdPartyInvoiceNumber',
  'thirdPartyInvoiceDate',
  'basicValue',
  'cgst',
  'sgst',
  'igst',
  'thirdPartyVendorCode',
];

/**
 * Additional Vendor Charges — Part 2 column-registry resolution (ISS-291749 / C2).
 *
 * This is the single place that turns the `GET /additional-charges/column-config` registry into
 * per-line rendering decisions. No component renders a hardcoded per-tab column list, and no component
 * re-implements the date rule or the rate-editability rule with its own conditionals — they read it
 * from here, which reads it from the registry.
 */

/** Looks up the column set for one `(chargeType, applicabilityLevel)` shape. */
export const getColumnsForShape = (
  shapes: AdditionalChargeColumnShape[],
  chargeType: string,
  applicabilityLevel: string
): AdditionalChargeColumn[] => {
  const shape = shapes.find(
    (candidate) =>
      candidate.chargeType === chargeType && candidate.applicabilityLevel === applicabilityLevel
  );
  return shape?.columns || [];
};

/** A line accepts edits only while in an editable status (DRAFT / REWORK) — see
 * EDITABLE_LINE_STATUSES. REJECTED is terminal (ISS-292248 review fix, CRITICAL 2): the backend's
 * bulk-PATCH throws "not mutable in status REJECTED" for a rejected line, so keeping REJECTED in
 * EDITABLE_LINE_STATUSES meant `buildSaveLines` re-sent it on every Save and the server rejected the
 * WHOLE batch — one rejected line bricked Save for every other line in the task. Only a REWORK line —
 * sent back by the approver for a fix — is editable again for re-submission. */
export const isLineEditable = (line: AdditionalChargeLineDto): boolean =>
  EDITABLE_LINE_STATUSES.includes(line.status);

/**
 * Resolves whether one column is editable for one line. This is the rule most likely to be got wrong
 * (contract §5.1): when a column carries `dynamicByRateSource`, the static `editable` flag is ignored
 * entirely and editability is derived from the line's own `rateSource` — `MANUAL` ⇒ editable,
 * `BID` ⇒ read-only. An Incidental charge whose catalog row has no `source_bid_charge_code` resolves to
 * `MANUAL` and must accept a typed rate despite Incidental's read-only default.
 *
 * `rateSource` is the ONLY per-line input to editability. `serviceStartDate` (PCD) briefly went through
 * a phase of being vendor-editable (D7); ISS-292248 reversed that — PCD is now auto-derived per vendor
 * type and frozen at submit, so its registry row carries `editable: false` again. No column is
 * special-cased by key in this file; the registry's static `editable` flag (or `dynamicByRateSource`)
 * is the only source of truth.
 */
export const isColumnEditableForLine = (
  column: AdditionalChargeColumn,
  line: AdditionalChargeLineDto,
  isReadOnly = false
): boolean => {
  if (isReadOnly || !isLineEditable(line)) {
    return false;
  }
  if (column.dynamicByRateSource) {
    return line.rateSource === 'MANUAL';
  }
  return column.editable;
};

/**
 * Whether one quantity dimension (`resolution.quantity.dims[]`) accepts a vendor-typed override
 * (ISS-292248 Task 4). A dim the engine sourced directly from a vendor input (`source ===
 * 'USER_INPUT'`) is always editable; a `COMPUTED` dim is editable only when the backend explicitly
 * flags it `overridable` — every other dim (e.g. a plain `COMPUTED` one with no override door) is
 * read-only. This is the single source of truth both the quantity-cell renderer
 * (`AdditionalChargeGridColumns.tsx`) and `buildSaveLines`'s per-dim filter below consult, so a dim
 * that renders read-only can never sneak a stale `quantityInputs` entry onto the wire.
 */
export const isQuantityDimEditable = (dim: AcQuantityDimResult): boolean =>
  dim.source === 'USER_INPUT' || dim.overridable === true;

/**
 * Filters out any charge type listed in `HIDDEN_CHARGE_TYPES` while preserving the groups' server-given
 * order. Third-Party Reimbursement (D2) was the one entry here, hidden pending A5; ISS-291743 unhid it
 * with a single deletion from that constant, because every renderer already covers all 6 shapes.
 */
export const getVisibleGroups = (groups: AdditionalChargeGroup[]): AdditionalChargeGroup[] =>
  groups.filter((group) => !HIDDEN_CHARGE_TYPES.includes(group.chargeType));

/**
 * The value a cell should display: an in-progress local edit if one exists for this line and field,
 * otherwise the line's own persisted value. Never sums or parses money — returns the string verbatim.
 * `field` is any registry entity column (read-only columns are looked up the same way; they simply
 * never have a draft entry).
 */
export const resolveLineFieldValue = (
  line: AdditionalChargeLineDto,
  draftEdits: AdditionalChargeLineDraftEdits,
  field: keyof AdditionalChargeLineDto
): string | null => {
  const draft = draftEdits[line.id];
  if (draft && Object.prototype.hasOwnProperty.call(draft, field)) {
    return (draft as Record<string, string | null | undefined>)[field as string] ?? null;
  }
  const value = line[field];
  return typeof value === 'string' ? value : null;
};

/** The money components summed into the Third-Party invoice value (ISS-291744). */
const THIRD_PARTY_INVOICE_VALUE_COMPONENTS: ReadonlyArray<keyof AdditionalChargeLineDto> = [
  'basicValue',
  'cgst',
  'sgst',
  'igst',
];

/**
 * The Third-Party `3rd Party Invoice Value (INR)` cell is a read-only running total, NOT a stored or
 * vendor-typed value (ISS-291744): Basic + CGST + SGST + IGST, taken from the current draft edit if one
 * exists for a component (else the persisted value), so the vendor watches the invoice value update as
 * they type. Only one of CGST/SGST/IGST is ever set per row (the `gst` exclusive group), so this equals
 * Basic + the single tax. The backend re-derives the authoritative value in SQL on save (ISS-291743) —
 * this is display only and never wired back.
 *
 * Blank / non-numeric components count as 0. Returns `null` when NONE of the four is numeric, so the
 * cell keeps its `-` empty state until the vendor starts entering money. The sum is rounded to 2 decimal
 * places to avoid binary-float artefacts (e.g. 0.1 + 0.2) leaking into the display.
 */
export const computeThirdPartyInvoiceTotal = (
  line: AdditionalChargeLineDto,
  draftEdits: AdditionalChargeLineDraftEdits
): string | null => {
  let hasNumericComponent = false;
  let total = 0;
  THIRD_PARTY_INVOICE_VALUE_COMPONENTS.forEach((field) => {
    const raw = resolveLineFieldValue(line, draftEdits, field);
    if (raw === null || raw.trim() === '') {
      return;
    }
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) {
      return;
    }
    hasNumericComponent = true;
    total += parsed;
  });
  if (!hasNumericComponent) {
    return null;
  }
  return String(Math.round(total * 100) / 100);
};

/**
 * True while a line's FX-derived figures — the live PCD, the live INR conversion — are still recomputed
 * on the fly (DRAFT/REWORK), as opposed to frozen at whatever was stored at submission. The single
 * exported predicate both `computeInrValue` (below) and `renderPcd`
 * (`AdditionalChargeGridColumns.tsx`) read, so the PCD cell and the INR cells can never disagree about
 * which PCD a live figure was converted at (ISS-292248 review fix, MINOR 5 — this used to be the same
 * boolean expression duplicated in both files).
 */
export const isFxLive = (line: AdditionalChargeLineDto): boolean =>
  line.status === 'DRAFT' || line.status === 'REWORK';

/** Discriminates WHY an INR figure can't be shown (ISS-292248 review fix, CRITICAL 1). `'ok'` — a real
 * converted number. `'pending-refresh'` — the effective (draft) currency differs from the line's
 * PERSISTED `currency` and `exchangeRates` simply predates this choice (the map is only ever seeded from
 * currencies already persisted on the lines); submit fetches its own rate server-side, so this is NOT a
 * blocked-submit state. `'unavailable'` — the currency IS the line's persisted one and there is still no
 * rate: genuinely no rate exists at the PCD, and the line really cannot be submitted like this. */
export type InrRateStatus = 'ok' | 'pending-refresh' | 'unavailable';

export interface InrCellValue {
  value: string | null;
  /** See `InrRateStatus`. Renderers MUST switch on this, not on `rateUnavailable` alone — otherwise a
   * newly-picked currency (`'pending-refresh'`) renders the alarming "cannot be submitted" wording it
   * hasn't earned. */
  rateStatus: InrRateStatus;
  /** True whenever `rateStatus !== 'ok'`. Kept so callers that only care about "not a plain number"
   * don't have to switch on `rateStatus` themselves — but the renderer itself must still branch on
   * `rateStatus` to pick the right wording. */
  rateUnavailable: boolean;
}

const inrCellResult = (value: string | null, rateStatus: InrRateStatus): InrCellValue => ({
  value,
  rateStatus,
  rateUnavailable: rateStatus !== 'ok',
});

/**
 * The read-only INR cell (ISS-292248): live while DRAFT/REWORK (`isFxLive`) — matching
 * `computeThirdPartyInvoiceTotal` — else the frozen `baseAmount` returned verbatim, exactly as
 * persisted. REJECTED is a terminal, no-longer-editable status (see `EDITABLE_LINE_STATUSES`) and is NOT
 * live here either: it shows the value frozen at its (rejected) submission, not a recomputed one.
 *
 * `kind` picks the base the INR figure is derived from while live: `'amount'` is rate × quantity
 * (Incidental / Self-Reimbursement's `Amount (INR)` column); `'invoiceValue'` is the Third-Party
 * running total from `computeThirdPartyInvoiceTotal` (its `Invoice Value (INR)` column). Both then
 * convert through `exchangeRates` the same way — identity for INR/blank currency, else a rate lookup
 * whose absence resolves to `'pending-refresh'` or `'unavailable'` per `InrRateStatus` above.
 *
 * Display-only float math is acceptable here by the same precedent as `computeThirdPartyInvoiceTotal` —
 * the authoritative freeze happens in SQL at submit.
 */
export const computeInrValue = (
  line: AdditionalChargeLineDto,
  draftEdits: AdditionalChargeLineDraftEdits,
  exchangeRates: Record<string, number>,
  kind: 'amount' | 'invoiceValue'
): InrCellValue => {
  if (!isFxLive(line)) {
    return inrCellResult(line.baseAmount, 'ok');
  }

  let base: number | null = null;
  if (kind === 'invoiceValue') {
    const total = computeThirdPartyInvoiceTotal(line, draftEdits);
    base = total === null ? null : Number(total);
  } else {
    const rate = resolveLineFieldValue(line, draftEdits, 'rate');
    if (rate !== null && rate.trim() !== '' && !Number.isNaN(Number(rate))) {
      const quantity = Number(line.quantity || '1') || 1;
      base = Number(rate) * quantity;
    }
  }
  if (base === null) {
    return inrCellResult(null, 'ok');
  }

  const currency = resolveLineFieldValue(line, draftEdits, 'currency');
  if (!currency || currency === 'INR') {
    return inrCellResult(String(Math.round(base * 100) / 100), 'ok');
  }
  const fx = exchangeRates[`${currency}-INR`];
  if (typeof fx !== 'number') {
    // exchangeRates is seeded ONLY from currencies already persisted on the lines (contract), so a rate
    // missing for the currency the vendor just picked in the draft — one that differs from what's still
    // persisted on the line — simply predates that choice, not a genuine absence of a rate at the PCD.
    const isNewlyPickedCurrency = currency !== line.currency;
    return inrCellResult(null, isNewlyPickedCurrency ? 'pending-refresh' : 'unavailable');
  }
  return inrCellResult(String(Math.round(base * fx * 100) / 100), 'ok');
};

/**
 * Maps each `exclusiveGroup` name to the entity-column fields of its member columns (e.g.
 * `{ gst: ['cgst', 'sgst', 'igst'] }`). Ungrouped columns are omitted. Registry-driven — no GST keys
 * are hardcoded; the grouping comes entirely from the backend column-config.
 */
export const buildExclusiveGroupMembers = (
  columns: AdditionalChargeColumn[]
): Record<string, string[]> => {
  const membersByGroup: Record<string, string[]> = {};
  columns.forEach((column) => {
    if (!column.exclusiveGroup) {
      return;
    }
    const field = column.entityColumn || column.key;
    if (!membersByGroup[column.exclusiveGroup]) {
      membersByGroup[column.exclusiveGroup] = [];
    }
    membersByGroup[column.exclusiveGroup].push(field);
  });
  return membersByGroup;
};

/**
 * Maps each `mirrorGroup` name to the entity-column fields of its member columns (ISS-292248), e.g.
 * `{ gstPair: ['cgst', 'sgst'] }`. Ungrouped columns are omitted. Registry-driven, same shape as
 * `buildExclusiveGroupMembers` — mirror pairs (CGST/SGST) additionally keep `exclusiveGroup: 'gst'`
 * against IGST, so a mirror member must not be locked by its own mirror partner (see
 * `isExclusiveMemberLocked` below).
 */
export const buildMirrorGroupMembers = (columns: AdditionalChargeColumn[]): Record<string, string[]> => {
  const membersByGroup: Record<string, string[]> = {};
  columns.forEach((column) => {
    if (!column.mirrorGroup) {
      return;
    }
    const field = column.entityColumn || column.key;
    if (!membersByGroup[column.mirrorGroup]) {
      membersByGroup[column.mirrorGroup] = [];
    }
    membersByGroup[column.mirrorGroup].push(field);
  });
  return membersByGroup;
};

/**
 * All fields a single edit must fan out to (ISS-292248): the field's whole mirror group when it has
 * one, else just itself. Resolved against ALL shapes once — mirror groups are global (e.g. `gstPair`
 * means the same thing everywhere it appears), not scoped per shape, so the first shape whose columns
 * contain the field decides the answer.
 *
 * This resolves against the first matching SHAPE, not the calling line's own shape — a deliberate
 * assumption (ISS-292248 review fix, MINOR 7) that every shape carrying one member of a mirror group
 * carries the WHOLE group (e.g. no shape defines `cgst` without also defining its `sgst` mirror
 * partner). If a future shape ever defines a partial mirror group, this would silently resolve the
 * fan-out against a DIFFERENT shape's full group, staging a field for a line whose own shape doesn't
 * carry it — `buildSaveLines` would then never emit that field (it only sends a field present in the
 * line's own shape), so the staged edit would be silently dropped on Save.
 */
export const getMirrorFieldsForField = (shapes: AdditionalChargeColumnShape[], field: string): string[] => {
  for (const shape of shapes) {
    const column = shape.columns.find((candidate) => (candidate.entityColumn || candidate.key) === field && candidate.mirrorGroup);
    if (column) {
      return shape.columns
        .filter((candidate) => candidate.mirrorGroup === column.mirrorGroup)
        .map((candidate) => candidate.entityColumn || candidate.key);
    }
  }
  return [field];
};

/**
 * True when `column` belongs to a mutual-exclusion group and a DIFFERENT member of that group already
 * holds a value on this line (persisted or a staged draft edit). Such a cell renders disabled/greyed —
 * the vendor clears the active member (which unlocks the rest) before entering another. The member that
 * itself holds the value is never locked, so it stays clearable.
 *
 * `mirrorGroupMembersByName` (ISS-292248, default `{}`) exempts a column's own mirror partners from
 * locking it: CGST and SGST mirror each other's value (see `getMirrorFieldsForField`), so one holding a
 * value must not grey out the other the way IGST — a genuine alternative, not a mirror — still does.
 */
export const isExclusiveMemberLocked = (
  column: AdditionalChargeColumn,
  line: AdditionalChargeLineDto,
  draftEdits: AdditionalChargeLineDraftEdits,
  groupMembersByName: Record<string, string[]>,
  mirrorGroupMembersByName: Record<string, string[]> = {}
): boolean => {
  if (!column.exclusiveGroup) {
    return false;
  }
  const members = groupMembersByName[column.exclusiveGroup] || [];
  const selfField = column.entityColumn || column.key;
  const partners = Object.values(mirrorGroupMembersByName).find((candidateMembers) => candidateMembers.includes(selfField)) || [];
  return members.some((memberField) => {
    if (memberField === selfField || partners.includes(memberField)) {
      return false;
    }
    const value = resolveLineFieldValue(line, draftEdits, memberField as keyof AdditionalChargeLineDto);
    return value !== null && value !== '';
  });
};

/**
 * Builds the `Save as Draft` bulk-PATCH body (ISS-291749). For a line the vendor actually TOUCHED this
 * session, "Save" deliberately overrides the server with whatever the grid currently shows rather than
 * diffing only the fields the vendor happened to change — so it re-sends the CURRENT value (staged
 * draft, else persisted) of every editable field on that line. A newly-uploaded attachment (staged
 * locally, not yet persisted) rides along for its line and also counts as a touch.
 *
 * A line with NEITHER a staged draft edit NOR a staged attachment is skipped entirely (ISS-292248 review
 * fix, IMPORTANT 3a) — there is nothing new to save, and re-sending its untouched current values is what
 * drags legacy invalidity that predates this feature (e.g. a pre-292248 row's one-sided GST, or a stored
 * free-text vendor code not in the master) into the request and 400s the WHOLE batch. This makes "click
 * Save with nothing edited" a silent no-op (see `onSaveDraft`'s `if (!lines.length) return`), not a
 * "nothing changed" warning.
 *
 * A field is sent only when it is (a) present in that line's own shape and (b) editable for that line,
 * so a BL-level line never receives a container-only PCD and a BID-rate line never receives a rate.
 * Every key in `BULK_PATCH_FIELD_KEYS` is wire-eligible (contract §3.3, extended by ISS-291743) —
 * `attachment` rides separately — so the third-party block (GST columns, vendor name/code, invoice
 * fields) is emitted for Third-Party Reimbursement lines exactly like any other editable column. Pass
 * only the VISIBLE groups — hidden charge types must not save.
 *
 * `thirdPartyVendors` (ISS-292248 review fix, IMPORTANT 3b) is the org's vendor-code master, from the
 * lines payload. A `thirdPartyVendorCode` whose resolved value is non-null and NOT in that master (a
 * legacy free-text code stored before this feature) is omitted from the patch entirely — sending it
 * 400s (`EADDCHARGE009`) even though the vendor never touched that field. Omitting it leaves the legacy
 * value untouched server-side instead of rejecting the whole batch. A vendor-picked code is always a
 * master entry (it comes from the same dropdown), so this never filters a genuine edit.
 *
 * Deliberately NOT done here (overruling a prior review suggestion): auto-normalising a one-sided
 * mirror-group GST value (e.g. a legacy `cgst: '90', sgst: null` row) by copying the non-null half
 * across both members. That would silently change a stored invoice total behind the vendor's back.
 * Untouched legacy GST values are left alone by the skip above; if the vendor edits that row, the mirror
 * fan-out (`getMirrorFieldsForField` / `onFieldChange`) sets both halves from their own input as normal.
 *
 * ISS-292248 Task 4: `draftQuantityInputs` (lineId → dim `code` → typed value) is a THIRD touch signal,
 * alongside a draft edit and a staged attachment — staging a dim value alone is enough to pull a line
 * into the patch, same as the other two. Its entries are filtered through `isQuantityDimEditable` against
 * the line's OWN live `resolution.quantity.dims` before they ride the wire, so a stale or no-longer-
 * editable dim (e.g. the engine re-resolved and the dim is no longer `overridable`) can never sneak onto
 * the request even if something left it in local state. `draftSelectorInputs` (same shape) is plumbed the
 * same way for `rateSelectorInputs` — no UI stages it yet (spec F3, the `USER_SELECT` dropdown is
 * deferred), so it is always empty in practice, but the parameter and its wire behaviour exist so the
 * eventual selector UI only has to stage into it.
 */
export const buildSaveLines = (
  groups: AdditionalChargeGroup[],
  shapes: AdditionalChargeColumnShape[],
  draftEdits: AdditionalChargeLineDraftEdits,
  draftAttachments: AdditionalChargeDraftAttachments,
  isReadOnly = false,
  thirdPartyVendors: Array<{ code: string; name: string }> = [],
  draftQuantityInputs: AdditionalChargeQuantityDraftInputs = {},
  draftSelectorInputs: AdditionalChargeQuantityDraftInputs = {}
): AdditionalChargeLinePatch[] => {
  if (isReadOnly) {
    return [];
  }
  const knownVendorCodes = new Set(thirdPartyVendors.map((vendor) => vendor.code));
  const patches: AdditionalChargeLinePatch[] = [];
  groups.forEach((group) => {
    group.charges.forEach((charge) => {
      const columns = getColumnsForShape(shapes, group.chargeType, charge.applicabilityLevel);
      charge.lines.forEach((line) => {
        if (!isLineEditable(line)) {
          return;
        }
        const draftForLine = draftEdits[line.id];
        const hasDraftEdit = !!draftForLine && Object.keys(draftForLine).length > 0;
        const hasDraftAttachment = Object.prototype.hasOwnProperty.call(draftAttachments, line.id);
        const draftQuantityForLine = draftQuantityInputs[line.id];
        const hasDraftQuantity = !!draftQuantityForLine && Object.keys(draftQuantityForLine).length > 0;
        const draftSelectorForLine = draftSelectorInputs[line.id];
        const hasDraftSelector = !!draftSelectorForLine && Object.keys(draftSelectorForLine).length > 0;
        if (!hasDraftEdit && !hasDraftAttachment && !hasDraftQuantity && !hasDraftSelector) {
          return;
        }
        const patch: AdditionalChargeLinePatch = { id: line.id };
        let hasWireField = false;
        BULK_PATCH_FIELD_KEYS.forEach((field) => {
          const column = columns.find((candidate) => candidate.key === field);
          if (!column || !isColumnEditableForLine(column, line, isReadOnly)) {
            return;
          }
          const value = resolveLineFieldValue(line, draftEdits, field as keyof AdditionalChargeLineDto);
          if (field === 'thirdPartyVendorCode' && value !== null && !knownVendorCodes.has(value)) {
            return;
          }
          patch[field] = value;
          hasWireField = true;
        });
        const pendingAttachment = draftAttachments[line.id];
        if (pendingAttachment) {
          patch.attachment = pendingAttachment;
          hasWireField = true;
        }
        if (hasDraftQuantity) {
          const editableDimCodes = new Set(
            (line.resolution?.quantity?.dims ?? [])
              .filter((dim) => isQuantityDimEditable(dim))
              .map((dim) => dim.code)
          );
          const quantityInputs: Record<string, string> = {};
          Object.keys(draftQuantityForLine as Record<string, string>).forEach((dimCode) => {
            if (editableDimCodes.has(dimCode)) {
              quantityInputs[dimCode] = (draftQuantityForLine as Record<string, string>)[dimCode];
            }
          });
          if (Object.keys(quantityInputs).length > 0) {
            patch.quantityInputs = quantityInputs;
            hasWireField = true;
          }
        }
        if (hasDraftSelector) {
          patch.rateSelectorInputs = { ...(draftSelectorForLine as Record<string, string>) };
          hasWireField = true;
        }
        if (hasWireField) {
          patches.push(patch);
        }
      });
    });
  });
  return patches;
};

/**
 * Splices freshly-returned lines (from a bulk PATCH response, or an attachment upload's follow-up
 * PATCH) back into the grouped payload by id, leaving every other line and the groups' order untouched.
 */
export const mergeUpdatedLines = (
  payload: AdditionalChargeLinesPayload,
  updatedLines: AdditionalChargeLineDto[]
): AdditionalChargeLinesPayload => {
  if (!updatedLines.length) {
    return payload;
  }
  const updatedById = updatedLines.reduce((acc, line) => {
    acc[line.id] = line;
    return acc;
  }, {} as Record<string, AdditionalChargeLineDto>);

  return {
    ...payload,
    groups: payload.groups.map((group) => ({
      ...group,
      charges: group.charges.map((charge) => ({
        ...charge,
        lines: charge.lines.map((line) => updatedById[line.id] || line),
      })),
    })),
  };
};
